
import api from "@/api";
import { DeviceDimensions } from "@/api/dtos/DeviceDimensions.schema";
import { DeviceTypeSettings } from "@/api/dtos/DeviceTypeSettings.schema";
import { Device } from "@/api/dtos/Station.schema";
import { LogEntry } from "@/api/dtos/LogEntry.schema";
import { Booking } from "@/api/dtos/Booking.schema";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import EditButton from "../buttons/EditButton.vue";
import CancelButton from "../buttons/CancelButton.vue";
import WarnButton from "../buttons/WarnButton.vue";
import DangerButton from "../buttons/DangerButton.vue";
import WarnDialog from "../dialogs/WarnDialog.vue";
import DangerDialog from "../dialogs/DangerDialog.vue";
import EditFrame from "../EditFrame.vue";
import DeviceConfiguration from "./DeviceConfiguration.vue";
import DeviceDescription from "./DeviceDescription.vue";
import DeviceDimensionsForm from "./DeviceDimensionsForm.vue";
import NumberOfSpaces from "./NumberOfSpaces.vue";
import RentalDuration from "./RentalDuration.vue";
import RentalMethodForm from "./RentalMethodForm.vue";
import TinyBookingsTable from "./TinyBookingsTable.vue";
import TinyLog from "./TinyLog.vue";
import moment from "moment";
import { StationsModule } from "@/store/modules/StationsModule";
import DeviceHardwareSettingsForm from "@/components/stationDetails/DeviceHardwareSettingsForm.vue";
import EditActionButtons from "../EditActionButtons.vue";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";
import { UsersModule } from "@/store/modules/UsersModule";
import { User } from "@/api/dtos/User.schema";
import { DeviceHardwareSettings } from "@/api/dtos/DeviceHardwareSettings.schema";
import { updateDeviceHardwareSettings } from "@/api/stations";

type EditableDevice = Device & {
  minInterBookingDuration?: string;
  maxPrebookDuration?: string;
  maxRentalDuration?: string;
  earlyGracePeriod?: string;
  lateGracePeriod?: string;
};

@Component({
  components: {
    EditFrame,
    EditButton,
    CancelButton,
    WarnButton,
    DangerButton,
    DeviceHardwareSettingsForm,
    RentalDuration,
    DeviceDescription,
    DeviceDimensionsForm,
    NumberOfSpaces,
    RentalMethodForm,
    DeviceConfiguration,
    TinyBookingsTable,
    TinyLog,
    WarnDialog,
    DangerDialog,
    EditActionButtons
  }
})
export default class DeviceSettingsDetails extends Vue {
  openTab = null;

  @Prop() device!: Device;
  @Prop() stationId!: string;
  @Prop({ default: false }) creationMode!: boolean;

  logEntries: LogEntry[] = [];
  bookings: Booking[] = [];
  showOpenDeviceDialog = false;
  openDeviceLoading = false;
  toggleActiveLoading = false;
  showToggleActiveDialog = false;
  showDeleteDeviceDialog = false;

  editMode = false;

  localChanges: EditableDevice = {} as EditableDevice;

  // Computed property to determine what data to display
  get displayData (): EditableDevice {
    return this.editMode ? this.localChanges : (this.device as EditableDevice);
  }

  get localUser (): User | null {
    return UsersModule.localUser;
  }

  get isAdmin (): boolean {
    return this.localUser?.isAdmin || false;
  }

  async mounted (): Promise<void> {
    await this.onRefreshClick();
    this.resetLocalChanges();
  }

  // Watch for device changes and reset localChanges accordingly
  @Watch("device", { deep: true, immediate: true })
  onDeviceChanged (newDevice: Device): void {
    console.log("Device updated from store/backend:", newDevice);
    if (!this.editMode) {
      this.resetLocalChanges();
    }
  }

  resetLocalChanges (): void {
    this.$set(this, "localChanges", {
      ...this.device,
      typeSettings: this.device.typeSettings || {},
      rentalMethod: this.device.rentalMethod || {},
      dimensions: this.device.dimensions || {},
      hardwareSettings: this.device.hardwareSettings || {}
    } as EditableDevice);
  }

  async onRefreshClick (): Promise<void> {
    if (this.creationMode) return;
    try {
      await StationsModule.refreshDetailedStation();
      const now = new Date();
      const lastWeek = moment().subtract(7, "days").toDate();

      if (this.localUser?.isAdmin) {
        this.logEntries = await api.LogApi.getLog(
          lastWeek,
          now,
          [this.stationId],
          ["BOX_CONFIG", "BOX_OPENING"]
        );
      }

      this.bookings = await api.BookingsApi.getBookings(lastWeek, now, [
        this.stationId
      ]);
    } catch (e) {
      console.error("Refresh error:", e);
    }
  }

  async onOpenDeviceClick (): Promise<void> {
    if (!this.device) return;
    this.showOpenDeviceDialog = false;
    this.openDeviceLoading = true;
    try {
      const success = await api.StationsApi.openDevice(
        this.stationId,
        this.device.deviceNumber
      );
      if (success) {
        await this.onRefreshClick();
      } else {
        showToast({
          message: "Die Box konnte nicht geöffnet werden.",
          type: "error"
        });
      }
    } catch (e) {
      console.error("Open device error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Die Box konnte nicht geöffnet werden.",
        type: "error"
      });
    }
    this.openDeviceLoading = false;
  }

  async onToggleDeviceActive (): Promise<void> {
    if (!this.device) return;
    this.showToggleActiveDialog = false;
    this.toggleActiveLoading = true;
    try {
      const success = await api.StationsApi.setDeviceActive(
        this.stationId,
        this.device.deviceNumber,
        !this.device.active
      );
      if (success) {
        await this.onRefreshClick();
      } else {
        showToast({
          message: "Der Boxstatus konnte nicht geändert werden.",
          type: "error"
        });
      }
    } catch (e) {
      console.error("Toggle device active state error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Der Boxstatus konnte nicht geändert werden.",
        type: "error"
      });
    }
    this.toggleActiveLoading = false;
  }

  onEdit (): void {
    this.resetLocalChanges();
    this.editMode = true;
  }

  onSave (): void {
    // Emit all the changes at once

    this.persistChanges();
    this.editMode = false;
  }

  onCancel (): void {
    this.resetLocalChanges();
    this.editMode = false;
  }

  // Helper method to emit all changes
  async persistChanges (): Promise<void> {
    if (this.hasDescriptionChanged()) {
      this.$emit("deviceDescriptionSave", {
        deviceNumber: this.device.deviceNumber,
        descriptionText: this.localChanges.descriptionText
      });
    }

    if (this.hasRentalMethodChanged()) {
      this.$emit("rentalMethodSave", {
        deviceNumber: this.device.deviceNumber,
        rentalMethod: this.localChanges.rentalMethod
      });
    }

    if (this.hasRentalTimeChanged()) {
      this.$emit("rentalTimeSave", {
        deviceNumber: this.device.deviceNumber,
        maxPrebookDuration: this.localChanges.maxPrebookDuration,
        maxRentalDuration: this.localChanges.maxRentalDuration,
        minInterBookingDuration: this.localChanges.minInterBookingDuration,
        earlyGracePeriod: this.localChanges.earlyGracePeriod,
        lateGracePeriod: this.localChanges.lateGracePeriod
      });
    }

    if (this.hasDimensionsChanged()) {
      this.$emit("deviceDimensionsSave", {
        deviceNumber: this.device.deviceNumber,
        deviceDimensions: this.localChanges.dimensions
      });
    }

    if (this.hasCapacityChanged()) {
      this.$emit("deviceCapacitySave", {
        deviceNumber: this.device.deviceNumber,
        capacity: this.localChanges.capacity
      });
    }

    if (this.hasDeviceHardwareSettingsChanged()) {
      this.$emit("deviceHardwareSettingsSave", {
        deviceNumber: this.device.deviceNumber,
        deviceHardwareSettings: this.localChanges.hardwareSettings
      });
    }

    if (this.hasTypeSettingsChanged()) {
      this.$emit("devicePropertiesSave", {
        deviceNumber: this.device.deviceNumber,
        deviceTypeSettings: this.localChanges.typeSettings
      });
    }
  }

  // Change detection methods
  private hasDescriptionChanged (): boolean {
    return this.device.descriptionText !== this.localChanges.descriptionText;
  }

  private hasRentalMethodChanged (): boolean {
    return (
      JSON.stringify(this.device.rentalMethod) !==
      JSON.stringify(this.localChanges.rentalMethod)
    );
  }

  private hasRentalTimeChanged (): boolean {
    const orig = this.device as EditableDevice;
    return (
      orig.maxPrebookDuration !== this.localChanges.maxPrebookDuration ||
      orig.maxRentalDuration !== this.localChanges.maxRentalDuration ||
      orig.minInterBookingDuration !== this.localChanges.minInterBookingDuration ||
      orig.earlyGracePeriod !== this.localChanges.earlyGracePeriod ||
      orig.lateGracePeriod !== this.localChanges.lateGracePeriod
    );
  }

  private hasDimensionsChanged (): boolean {
    return (
      JSON.stringify(this.device.dimensions) !==
      JSON.stringify(this.localChanges.dimensions)
    );
  }

  private hasCapacityChanged (): boolean {
    return this.device.capacity !== this.localChanges.capacity;
  }

  private hasDeviceHardwareSettingsChanged (): boolean {
    return (
      JSON.stringify(this.device.hardwareSettings) !==
      JSON.stringify(this.localChanges.hardwareSettings)
    );
  }

  private hasTypeSettingsChanged (): boolean {
    return (
      JSON.stringify(this.device.typeSettings) !==
      JSON.stringify(this.localChanges.typeSettings)
    );
  }

  // Update methods for child components

  updateDescription (value: string) {
    this.localChanges.descriptionText = value ?? "";
  }

  updateRentalMethod (value: Device["rentalMethod"]): void {
    this.localChanges.rentalMethod = value;
  }

  updateRentalDuration (values: {
    maxPrebookDuration: string;
    maxRentalDuration: string;
    minInterBookingDuration: string;
    earlyGracePeriod: string;
    lateGracePeriod: string;
  }): void {
    this.localChanges.maxPrebookDuration = values.maxPrebookDuration;
    this.localChanges.maxRentalDuration = values.maxRentalDuration;
    this.localChanges.minInterBookingDuration = values.minInterBookingDuration;
    this.localChanges.earlyGracePeriod = values.earlyGracePeriod;
    this.localChanges.lateGracePeriod = values.lateGracePeriod;
  }

  updateDimensions (value: DeviceDimensions): void {
    this.localChanges.dimensions = value;
  }

  updateCapacity (value: number): void {
    this.localChanges.capacity = value;
  }

  updateDeviceHardwareSettings (value: DeviceHardwareSettings): void {
    this.localChanges.hardwareSettings = value;
  }

  updateTypeSettings (value: DeviceTypeSettings): void {
    this.localChanges.typeSettings = value;

    console.log("this.device.descriptionText", this.localChanges.typeSettings);
  }

  onDeviceDeletedClick (): void {
    this.$emit("deviceDeleted", this.device?.deviceNumber);
  }
}
